import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Plus, Save, Trash2, ArrowLeft, ExternalLink, Check } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { value: 'video',     label: 'Video' },
  { value: 'pdf',       label: 'PDF / eBook' },
  { value: 'ea',        label: 'EA / Robot' },
  { value: 'indicator', label: 'Indicator' },
  { value: 'doc',       label: 'Documentation' },
  { value: 'other',     label: 'Other' },
];

function emptyItem() {
  return { category: 'pdf', title: '', file_url: '', description: '', size: '' };
}

export default function AdminCoursesPage() {
  const { user, loading: authLoading } = useAuth();
  const [products, setProducts] = useState([]);
  const [selectedSlug, setSelectedSlug] = useState('');
  const [content, setContent] = useState({ display_name: '', subtitle: '', items: [emptyItem()] });
  const [savedFlash, setSavedFlash] = useState(false);
  const [saving, setSaving] = useState(false);

  // Load product list
  useEffect(() => {
    if (!user || user.role !== 'admin') return;
    axios.get(`${API}/api/admin/course-list`, { withCredentials: true })
      .then((res) => setProducts(res.data.products || []))
      .catch(() => {});
  }, [user]);

  // Load selected course content
  useEffect(() => {
    if (!selectedSlug) return;
    axios.get(`${API}/api/admin/course-content/${selectedSlug}`, { withCredentials: true })
      .then((res) => {
        const c = res.data;
        setContent({
          display_name: c.display_name || products.find(p => p.slug === selectedSlug)?.name || selectedSlug,
          subtitle: c.subtitle || '',
          items: (c.items && c.items.length) ? c.items : [emptyItem()],
        });
      })
      .catch(() => {});
  }, [selectedSlug, products]);

  if (authLoading) return <div className="min-h-screen pt-24 text-center text-white/50">Loading...</div>;
  if (!user) {
    return (
      <div className="min-h-screen pt-32 pb-20 px-6 text-center">
        <h1 className="text-2xl font-bold text-white">Admin only</h1>
        <Link to="/login" className="mt-4 inline-block text-amber-400">Sign in →</Link>
      </div>
    );
  }
  if (user.role !== 'admin') {
    return (
      <div className="min-h-screen pt-32 pb-20 px-6 text-center" data-testid="admin-forbidden">
        <h1 className="text-2xl font-bold text-white">Access denied</h1>
        <p className="mt-2 text-sm text-white/50">This page is admin-only.</p>
      </div>
    );
  }

  const updateItem = (i, patch) => {
    const next = [...content.items];
    next[i] = { ...next[i], ...patch };
    setContent({ ...content, items: next });
  };
  const removeItem = (i) => {
    if (content.items.length === 1) { setContent({ ...content, items: [emptyItem()] }); return; }
    setContent({ ...content, items: content.items.filter((_, idx) => idx !== i) });
  };
  const addItem = () => setContent({ ...content, items: [...content.items, emptyItem()] });

  const save = async () => {
    if (!selectedSlug) return;
    setSaving(true);
    try {
      const payload = {
        display_name: content.display_name,
        subtitle: content.subtitle,
        items: content.items.filter(it => it.title && it.file_url),
      };
      await axios.put(`${API}/api/admin/course-content/${selectedSlug}`, payload, { withCredentials: true });
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 2000);
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen pt-24 pb-20" data-testid="admin-courses-page">
      <section className="px-4 sm:px-6 lg:px-8 pb-10">
        <div className="mx-auto max-w-5xl">
          <Link to="/" className="inline-flex items-center gap-1.5 text-xs text-white/40 hover:text-white/70 transition-colors mb-5">
            <ArrowLeft size={13} /> Back to site
          </Link>
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-amber-400/80 mb-2">Admin</p>
          <h1 className="text-3xl sm:text-4xl font-bold text-white tracking-tight">Course Content Manager</h1>
          <p className="mt-3 text-sm text-white/50">Upload your files to Dropbox, then paste the share links below. Buyers see this content on their <code className="text-amber-400">/my-courses</code> page the instant they pay.</p>
        </div>
      </section>

      {/* Product picker */}
      <section className="px-4 sm:px-6 lg:px-8 pb-6">
        <div className="mx-auto max-w-5xl">
          <h2 className="text-xs font-bold uppercase tracking-wider text-white/40 mb-3">Choose a course</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {products.map((p) => {
              const active = p.slug === selectedSlug;
              return (
                <button
                  key={p.slug}
                  onClick={() => setSelectedSlug(p.slug)}
                  className={`text-left rounded-xl border p-4 transition-colors ${active ? 'border-amber-400/40 bg-amber-400/[0.06]' : 'border-white/[0.06] bg-white/[0.02] hover:border-amber-400/20'}`}
                  data-testid={`product-pick-${p.slug}`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-sm font-bold text-white truncate">{p.name}</p>
                    {p.delivery_item_count > 0 && <span className="text-[10px] text-emerald-400 font-bold">✓ {p.delivery_item_count}</span>}
                  </div>
                  <p className="text-[11px] text-white/40 font-mono">{p.slug}</p>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Editor */}
      {selectedSlug && (
        <section className="px-4 sm:px-6 lg:px-8" data-testid="content-editor">
          <div className="mx-auto max-w-5xl rounded-2xl border border-white/[0.06] bg-white/[0.02] p-6 sm:p-8">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
              <div>
                <label className="text-[10px] font-semibold uppercase tracking-wider text-white/40">Display name</label>
                <input
                  className="mt-1.5 w-full rounded-lg bg-[#050814] border border-white/10 px-4 py-2.5 text-sm text-white focus:outline-none focus:border-amber-400/40"
                  value={content.display_name}
                  onChange={(e) => setContent({ ...content, display_name: e.target.value })}
                  data-testid="input-display-name"
                />
              </div>
              <div>
                <label className="text-[10px] font-semibold uppercase tracking-wider text-white/40">Subtitle</label>
                <input
                  className="mt-1.5 w-full rounded-lg bg-[#050814] border border-white/10 px-4 py-2.5 text-sm text-white focus:outline-none focus:border-amber-400/40"
                  value={content.subtitle}
                  onChange={(e) => setContent({ ...content, subtitle: e.target.value })}
                  placeholder="e.g. Full Day Trading Automation"
                  data-testid="input-subtitle"
                />
              </div>
            </div>

            <h3 className="text-xs font-bold uppercase tracking-wider text-white/40 mb-3">Files</h3>
            <div className="space-y-3">
              {content.items.map((it, i) => (
                <div key={i} className="rounded-xl border border-white/[0.06] bg-[#050814] p-4" data-testid={`item-row-${i}`}>
                  <div className="grid grid-cols-1 sm:grid-cols-12 gap-3">
                    <select
                      value={it.category}
                      onChange={(e) => updateItem(i, { category: e.target.value })}
                      className="sm:col-span-2 rounded-lg bg-[#0a0f1c] border border-white/10 px-3 py-2 text-xs text-white focus:outline-none focus:border-amber-400/40"
                      data-testid={`item-category-${i}`}
                    >
                      {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                    </select>
                    <input
                      placeholder="Title (e.g. Course Video — 9-Step Pro)"
                      value={it.title}
                      onChange={(e) => updateItem(i, { title: e.target.value })}
                      className="sm:col-span-4 rounded-lg bg-[#0a0f1c] border border-white/10 px-3 py-2 text-xs text-white placeholder:text-white/30 focus:outline-none focus:border-amber-400/40"
                      data-testid={`item-title-${i}`}
                    />
                    <input
                      placeholder="Dropbox link (https://www.dropbox.com/...)"
                      value={it.file_url}
                      onChange={(e) => updateItem(i, { file_url: e.target.value })}
                      className="sm:col-span-4 rounded-lg bg-[#0a0f1c] border border-white/10 px-3 py-2 text-xs text-white font-mono placeholder:text-white/30 focus:outline-none focus:border-amber-400/40"
                      data-testid={`item-url-${i}`}
                    />
                    <input
                      placeholder="Size (e.g. 62.9 MB)"
                      value={it.size}
                      onChange={(e) => updateItem(i, { size: e.target.value })}
                      className="sm:col-span-1 rounded-lg bg-[#0a0f1c] border border-white/10 px-3 py-2 text-xs text-white placeholder:text-white/30 focus:outline-none focus:border-amber-400/40"
                      data-testid={`item-size-${i}`}
                    />
                    <button
                      onClick={() => removeItem(i)}
                      className="sm:col-span-1 inline-flex items-center justify-center px-3 py-2 rounded-lg text-white/40 hover:text-red-400 hover:bg-red-400/10 transition-colors"
                      title="Remove"
                      data-testid={`item-remove-${i}`}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                  <input
                    placeholder="Description (optional)"
                    value={it.description}
                    onChange={(e) => updateItem(i, { description: e.target.value })}
                    className="mt-2 w-full rounded-lg bg-[#0a0f1c] border border-white/10 px-3 py-2 text-xs text-white placeholder:text-white/30 focus:outline-none focus:border-amber-400/40"
                    data-testid={`item-desc-${i}`}
                  />
                  {it.file_url && (
                    <a href={it.file_url} target="_blank" rel="noreferrer" className="mt-2 inline-flex items-center gap-1 text-[10px] text-amber-400/70 hover:text-amber-400">
                      <ExternalLink size={10} /> Test link
                    </a>
                  )}
                </div>
              ))}
            </div>

            <div className="mt-5 flex items-center justify-between flex-wrap gap-3">
              <button
                onClick={addItem}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-white/15 text-white/70 text-xs font-semibold hover:border-amber-400/40 hover:text-amber-400 transition-colors"
                data-testid="add-item-btn"
              >
                <Plus size={13} /> Add file
              </button>
              <button
                onClick={save}
                disabled={saving}
                className={`inline-flex items-center gap-2 px-6 py-3 rounded-lg font-bold text-sm transition-colors ${savedFlash ? 'bg-emerald-400 text-black' : 'bg-amber-400 text-black hover:bg-amber-300'} disabled:opacity-50`}
                data-testid="save-btn"
              >
                {savedFlash ? <><Check size={15} /> Saved</> : saving ? 'Saving…' : <><Save size={15} /> Save course</>}
              </button>
            </div>

            <div className="mt-6 rounded-xl border border-sky-400/20 bg-sky-400/[0.04] p-4 text-xs text-white/60 leading-relaxed">
              <p className="font-bold text-sky-400 mb-1">💡 Dropbox link tip</p>
              <p>Right-click any file in Dropbox → <em>Share</em> → <em>Copy link</em>. Paste that link here exactly as Dropbox gives it to you — the system handles the rest. Your buyers can stream/download but the original file stays safely in your Dropbox.</p>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
